import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { Restaurant, RestaurantDetail } from './data';
import { restaurantService } from './service';

export type LoginParams = {
  name: string;
};

type State = {
  currentRestaurant?: RestaurantDetail | Restaurant ;
  restaurants: Restaurant[];
  isLoading: boolean;
};
const lsCurrentRestaurant = localStorage.getItem('currentRestaurant');
const currentRestaurantCached =
  typeof lsCurrentRestaurant === 'string'
    ? JSON.parse(lsCurrentRestaurant)
    : undefined;

const initialState: State = {
  currentRestaurant: currentRestaurantCached,
  restaurants: [],
  isLoading: false,
};

export const fetchRestaurants = createAsyncThunk<
  {
    restaurants: Restaurant[];
    currentRestaurant?: RestaurantDetail;
  },
  undefined,
  { rejectValue: string }
>('restaurant', async (_, thunkAPI) => {
  try {
    const res = await restaurantService.get();
    const restaurants = res.data.data?.data;
    if (restaurants.length === 0) {
      return {
        restaurants,
      };
    }

    const currentRestaurantId = currentRestaurantCached?.id || restaurants[0]?.id;
    if (!currentRestaurantId) throw new Error("No valid restaurant found");

    const resGetById = await restaurantService.getById(
      currentRestaurantId
    );
    if (resGetById.data.message !== "success") throw new Error("Cannot get CurrentRestaurant");

    return { restaurants, currentRestaurant: resGetById.data.data };

  } catch (error: any) {
    const message =
      (error.response && error.response.data && error.response.data.message) ||
      error.message ||
      error.toString();
    return thunkAPI.rejectWithValue(message);
  }
});

export const fetchCurrentRestaurantById = createAsyncThunk<
  RestaurantDetail,
  number,
  { rejectValue: string }
>('current-restaurant', async (restaurantId, thunkAPI) => {
  try {
    const resGetById = await restaurantService.getById(restaurantId);
    if (resGetById.data.message === 'success') {
      const currentRestaurant = resGetById.data.data;
      return currentRestaurant;
    }
    throw new Error('Cannot get Restaurant List or CurrentRestaurant Info!');
  } catch (error: any) {
    const message =
      (error.response && error.response.data && error.response.data.message) ||
      error.message ||
      error.toString();
    return thunkAPI.rejectWithValue(message);
  }
});

const restaurantSlice = createSlice({
  name: 'restaurant',
  initialState,
  reducers: {
    setCurrentRestaurant: (state, action: PayloadAction<RestaurantDetail | Restaurant>) => {
      if (!action.payload) return;
      localStorage.setItem('currentRestaurant', JSON.stringify(action.payload));
      state.currentRestaurant = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchRestaurants.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(fetchRestaurants.fulfilled, (state, action) => {
      state.isLoading = false;
      state.restaurants = action.payload.restaurants;
      state.currentRestaurant = action.payload.currentRestaurant;
      localStorage.setItem(
        'currentRestaurant',
        JSON.stringify(action.payload.currentRestaurant),
      );
    });
    builder.addCase(fetchRestaurants.rejected, (state) => {
      state.isLoading = false;
      state.restaurants = [];
      state.currentRestaurant = undefined;
    });
    builder.addCase(fetchCurrentRestaurantById.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(fetchCurrentRestaurantById.fulfilled, (state, action) => {
      state.isLoading = false;
      state.currentRestaurant = action.payload;
      localStorage.setItem('currentRestaurant', JSON.stringify(action.payload));
    });
    builder.addCase(fetchCurrentRestaurantById.rejected, (state) => {
      state.isLoading = false;
      state.currentRestaurant = undefined;
    });
  },
});

export const { setCurrentRestaurant } = restaurantSlice.actions;
export default restaurantSlice;
